import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { validationRules, formatPhoneNumber, formatEmail } from '../utils/formValidation';
import { getEmailRecipient, getCCEmailRecipient, addEnvironmentInfo } from '../utils/emailRouting';
import { metaPixel } from '../utils/metaPixelTracking';

const ContactFormRHF = ({
  toEmail,
  slug = null,
  arrivalInfo = null,
  sendMarketingEmail = false,
  appendToBody = "",
  uid = null,
  sessionId = null,
  communityName = null,
}) => {
  const [response, setResponse] = useState("");
  const [notification, setNotification] = useState("");
  const [mailpass, setMailpass] = useState("");
  const [mailuser, setMailuser] = useState("");
  const [sending, setSending] = useState(false);
  const [hasBeenSent, setHasBeenSent] = useState(false);

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    reset
  } = useForm({
    defaultValues: {
      fname: "",
      lname: "",
      email: "",
      phone: "",
      message: "",
      agreeToTextMessages: false
    },
    mode: "onChange" // Validate on change for better UX
  });

  // Watch phone and email fields for formatting
  const phoneValue = watch("phone");
  const emailValue = watch("email");

  // Format phone number on change
  useEffect(() => {
    if (phoneValue) {
      const formatted = formatPhoneNumber(phoneValue);
      if (formatted !== phoneValue) {
        setValue("phone", formatted);
      }
    }
  }, [phoneValue, setValue]);

  // Format email on change
  useEffect(() => {
    if (emailValue) {
      const formatted = formatEmail(emailValue);
      if (formatted !== emailValue) {
        setValue("email", formatted);
      }
    }
  }, [emailValue, setValue]);

  useEffect(() => {
    let mailpass = sessionStorage.getItem("mailpass");
    let mailuser = sessionStorage.getItem("mailuser");
    if (mailpass && mailpass.length > 0 && mailuser && mailuser.length > 0) {
      setMailpass(mailpass);
      setMailuser(mailuser);
    } else {
      getAuth(
        "https://mailut.mcklab.com/?action=login&username=orangepup&password=" +
          createPass(),
      );
    }
  }, []);

  const createPass = () => {
    let date = new Date();
    let dateString = date.toISOString().slice(0, 10);
    let pass = "froglegs";
    let encodedString = btoa(dateString + pass);
    return encodedString;
  };

  const getAuth = (url) => {
    fetch(url)
      .then((response) => response.json())
      .then((res) => {
        if (res.message_type == "success") {
          setMailpass(res.loggedin_pass);
          setMailuser(res.loggedin_user);
          sessionStorage.setItem("mailpass", res.loggedin_pass);
          sessionStorage.setItem("mailuser", res.loggedin_user);
        } else {
          setResponse(
            "We were not able to authorize this form to send emails. Please try again later",
          );
          setNotification("is-warning");
        }
      });
  };

  const sendMail = (url, emailData, formData) => {
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": "tenOrangePups22",
      },
      body: JSON.stringify(emailData),
    })
      .then((response) => response.json())
      .then((res) => {
        console.log(res);
        if (res.message_type == "success" || res.errorCode == 200) {
          setResponse("Message sent successfully");
          setNotification("is-success");
          reset(); // Reset form on success
          
          // Track Meta Pixel Lead event for contact form submission
          metaPixel.trackLead({
            content_name: communityName || slug || 'Contact Form',
            content_category: 'Contact Form Submission',
            property_slug: slug,
            form_type: 'contact_form',
            form_location: uid || 'unknown',
            has_message: formData && formData.message && formData.message.trim().length > 0
          });
        } else {
          setResponse("Sorry, we were not able to send a message at this time");
          setNotification("is-danger");
        }
        setSending(false);
        setHasBeenSent(true);
      });
  };

  const onSubmit = (data) => {
    setSending(true);

    // Use environment-based email routing with UTM source
    const recipientEmail = getEmailRecipient(toEmail, slug, arrivalInfo?.utmSource);
    const ccEmail = getCCEmailRecipient(slug);

    // Prepare base email body
    const baseBody = `Full Name: ${data.fname.trim()} ${data.lname.trim()} \n
                      Email: ${data.email.trim()} \n
                      Phone: ${data.phone.replace(/\D/g, "")} \n
                      Notes: ${appendToBody ? data.message.trim() + " \n" + appendToBody : data.message.trim()}`;

    // Add environment and tracking info
    const fullBody = addEnvironmentInfo(baseBody, {
      utmSource: arrivalInfo?.utmSource,
      utmMedium: arrivalInfo?.utmMedium,
      utmCampaign: arrivalInfo?.utmCampaign,
      referrer: arrivalInfo?.referrer,
      sessionId: sessionId
    });

    let sendData = {
      loggedin_pass: mailpass,
      loggedin_user: mailuser,
      action: "mail",
      to: recipientEmail,
      cc: ccEmail,
      name: data.fname.trim() + " " + data.lname.trim(),
      subject:
        (communityName ? `${communityName} - ` : "") +
        "McKinley.com Contact Form Message from " +
        data.fname.trim() +
        " " +
        data.lname.trim(),
      body: fullBody,
    };

    let endpoint = "https://mailut.mcklab.com";
    sendMail(endpoint, sendData, data);
  };

  return (
    <div id="contact_form">
      {response && (
        <div className={notification + " notification"}>{response}</div>
      )}
      <br />
      
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* First Name Field */}
        <div className="field">
          <label className="label">First Name*</label>
          <div className="control">
            <input
              {...register("fname", validationRules.firstName)}
              className={`input ${errors.fname ? 'is-danger' : ''}`}
              type="text"
              placeholder="e.g Alex"
            />
          </div>
          {errors.fname && (
            <p className="help is-danger">{errors.fname.message}</p>
          )}
        </div>

        {/* Last Name Field */}
        <div className="field">
          <label className="label">Last Name*</label>
          <div className="control">
            <input
              {...register("lname", validationRules.lastName)}
              className={`input ${errors.lname ? 'is-danger' : ''}`}
              type="text"
              placeholder="e.g Smith"
            />
          </div>
          {errors.lname && (
            <p className="help is-danger">{errors.lname.message}</p>
          )}
        </div>

        {/* Email Field */}
        <div className="field">
          <label className="label">Email*</label>
          <div className="control">
            <input
              {...register("email", validationRules.email)}
              className={`input ${errors.email ? 'is-danger' : ''}`}
              type="email"
              placeholder="e.g. alexsmith@gmail.com"
            />
          </div>
          {errors.email && (
            <p className="help is-danger">{errors.email.message}</p>
          )}
        </div>

        {/* Phone Field */}
        <div className="field">
          <label className="label">Phone*</label>
          <div className="control">
            <input
              {...register("phone", validationRules.phone)}
              className={`input ${errors.phone ? 'is-danger' : ''}`}
              type="text"
              placeholder="e.g 555-555-5555"
            />
          </div>
          {errors.phone && (
            <p className="help is-danger">{errors.phone.message}</p>
          )}
        </div>

        {/* Message Field */}
        <div className="field">
          <label className="label">Message*</label>
          <div className="control">
            <textarea
              {...register("message", validationRules.message)}
              className={`textarea ${errors.message ? 'is-danger' : ''}`}
              placeholder="e.g. How many bedrooms, budget, desired move-in date, etc."
            ></textarea>
          </div>
          {errors.message && (
            <p className="help is-danger">{errors.message.message}</p>
          )}
        </div>

        {/* Consent Checkbox */}
        <div className="field">
          <div className="control">
            <label className="checkbox">
              <input
                {...register("agreeToTextMessages", validationRules.agreeToTextMessages)}
                type="checkbox"
                className={errors.agreeToTextMessages ? 'is-danger' : ''}
              />
              &nbsp;By checking this box you agree to our Privacy Policy <br />
              and to receive text messages from McKinley. <br />
              Reply STOP to opt out
            </label>
            {errors.agreeToTextMessages && (
              <p className="help is-danger">{errors.agreeToTextMessages.message}</p>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="field">
          {!sending && !hasBeenSent && (
            <button
              type="submit"
              className={uid ? `${uid} button is-link` : "button is-link"}
              disabled={isSubmitting}
            >
              Submit
            </button>
          )}
          {sending && <p>Sending...</p>}
          {hasBeenSent && <p>Thank you!</p>}
        </div>
      </form>

      <br />
    </div>
  );
};

export default ContactFormRHF;